import React, { Component, createRef } from "react";
import * as d3 from "d3";
import noDataPlaceholder from "../../assets/img/nodata_available.svg";

export interface ChartData {
  label: string;
  value: number;
  diff?: number | null;
}
interface Props {
  data: ChartData[];
  barColor?: string;
  valueColor?: string;
  labelColor?: string;
  width?: number;
  height?: number;
}

class VerticalPercentageBarChart extends Component<Props> {
  private svgRef = createRef<SVGSVGElement>();
  private tooltipDiv: HTMLDivElement | null = null;

  componentDidMount() {
    this.ensureTooltipDiv();
    this.renderChart();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.data !== this.props.data ||
      prevProps.barColor !== this.props.barColor ||
      prevProps.valueColor !== this.props.valueColor ||
      prevProps.labelColor !== this.props.labelColor ||
      prevProps.width !== this.props.width ||
      prevProps.height !== this.props.height
    ) {
      this.renderChart();
    }
  }

  componentWillUnmount(): void {
    // Clean up tooltip div on unmount
    if (this.tooltipDiv && document.body.contains(this.tooltipDiv)) {
      document.body.removeChild(this.tooltipDiv);
    }
    this.tooltipDiv = null;
  }

  /** Create a reusable floating tooltip div once */
  private ensureTooltipDiv() {
    if (this.tooltipDiv && document.body.contains(this.tooltipDiv)) return;

    const div = document.createElement("div");
    div.style.position = "fixed";
    div.style.pointerEvents = "none";
    div.style.zIndex = "9999";
    div.style.background = "rgba(0,0,0,0.85)";
    div.style.color = "#fff";
    div.style.padding = "6px 8px";
    div.style.borderRadius = "6px";
    div.style.fontFamily = "Poppins, sans-serif";
    div.style.fontSize = "12px";
    div.style.lineHeight = "1.3";
    div.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
    div.style.opacity = "0";
    div.style.transform = "translate(-50%, -120%)";
    div.style.transition = "opacity 120ms ease-out";
    document.body.appendChild(div);
    this.tooltipDiv = div;
  }

  /** Truncate label and add ellipsis if longer than max */
  private truncateLabel = (text: string, max = 10) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "…" : text;
  };

  /** Escape HTML for tooltip content safety */
  private escapeHtml(str: string) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /** Show tooltip with (label, value) at mouse position */
  private showTooltip = (label: string, value: number, diff: number | null, evt: MouseEvent) => {
    if (!this.tooltipDiv) return;
    const arrow =
      diff === null || diff === 0
        ? ""
        : diff > 0
        ? "▲"
        : "▼";

    this.tooltipDiv.innerHTML = `
      <div style="font-weight:600; margin-bottom:2px;">${this.escapeHtml(
      label
    )}</div>
      <div style="opacity:0.9;">${value}% ${diff !== null ? `(${arrow}${Math.abs(diff)})` : ""}</div>
    `;
    this.tooltipDiv.style.opacity = "1";

    // Position near mouse; keep within viewport
    const padding = 10;
    const { clientX, clientY } = evt;
    const rect = this.tooltipDiv.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = clientX + 12;
    let y = clientY - 12;

    if (x + rect.width + padding > vw) x = vw - rect.width - padding;
    if (y + rect.height + padding > vh) y = vh - rect.height - padding;
    if (y < padding) y = padding;

    this.tooltipDiv.style.left = `${x}px`;
    this.tooltipDiv.style.top = `${y}px`;
  };

  /** Hide tooltip */
  private hideTooltip = () => {
    if (!this.tooltipDiv) return;
    this.tooltipDiv.style.opacity = "0";
  };

  private wrapText = (
    textSelection: d3.Selection<SVGTextElement, string, SVGGElement, unknown>,
    maxWidth: number,
    maxLines: number = 3
  ) => {
    textSelection.each(function (d) {
      const textSel = d3.select(this);

      // Force everything to string
      const rawText = d != null ? String(d) : textSel.text();
      if (!rawText || rawText.trim().length === 0) return;

      const words = rawText.split(/\s+/);
      if (words.length === 0) return;

      let line: string[] = [];
      let lineNumber = 0;
      const lineHeight = 1.1;

      textSel.text(null);
      let tspan = textSel.append("tspan").attr("x", 0).attr("dy", "0em");

      for (let i = 0; i < words.length; i++) {
        line.push(words[i]);
        tspan.text(line.join(" "));
        const tooWide = (tspan.node() as SVGTextElement).getComputedTextLength() > maxWidth;

        if (tooWide) {
          line.pop();
          tspan.text(line.join(" "));
          lineNumber++;

          if (lineNumber >= maxLines) {
            let finalText = line.join(" ") + "…";
            tspan.text(finalText);

            while (
              finalText.length > 0 &&
              (tspan.node() as SVGTextElement).getComputedTextLength() > maxWidth
              ) {
              finalText = finalText.slice(0, -2) + "…";
              tspan.text(finalText);
            }
            return;
          }

          line = [words[i]];
          tspan = textSel.append("tspan")
            .attr("x", 0)
            .attr("dy", `${lineHeight}em`)
            .text(words[i]);
        }
      }
    });
  };


  renderChart = () => {
    const {
      data,
      barColor = "#2F4858",
      valueColor = "#333",
      labelColor = "#333",
      width = 600,
      height = 400,
    } = this.props;

    if (!data || !data.length) return;

    const normalizedData = data.map((d: any) => ({
      label: d.label ?? d.brand ?? d.product_nm ?? "",
      value: d.value ?? d.cubic_share_p1 ?? 0,
      diff: d.cubic_share_diff ?? null
    }));

    const margin = { top: 20, right: 40, bottom: 60, left: 40 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3.select(this.svgRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMinYMin meet")
      .style("width", "100%")
      .style("height", "auto");

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X axis (labels)
    const x = d3
      .scaleBand<string>()
      .domain(normalizedData.map((d) => d.label))
      .range([0, innerWidth])
      .padding(0.3);

    // --- X Axis with truncated labels and tooltip on hover ---
    const xAxis = d3
      .axisBottom(x)
      .tickSize(0)
      // .tickFormat((d: string) => this.truncateLabel(d, 10));
      .tickFormat((d: string) => d);

    const xAxisG = chart
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(xAxis);

    xAxisG.call((g) => g.select(".domain").remove());

    xAxisG
      .selectAll<SVGTextElement, string>("text")
      .style("fill", labelColor)
      .style("font-family", "Poppins, sans-serif")
      .style("font-weight", "400")
      .style("font-size", "12px")
      .style("line-height", "14px")
      .style("text-anchor", "middle")
      .attr("dy", "1.6em")   // pushes first line down
      .attr("y", 20)
      .call((text) => this.wrapText(text, x.bandwidth(), 3))
      // Add native <title> fallback with full label
      .each(function (d: string) {
        const text = d3.select(this);
        text.select("title").remove();
        text.append("title").text(d);
      })
      // Custom tooltip: label + value
      .on("mouseover", (event: MouseEvent, d: string) => {
        const datum = normalizedData.find((x) => x.label === d);
        if (!datum) return;
        this.showTooltip(datum.label, datum.value, datum.diff ?? null, event);
      })
      .on("mousemove", (event: MouseEvent, d: string) => {
        const datum = normalizedData.find((x) => x.label === d);
        if (!datum) return;
        this.showTooltip(datum.label, datum.value, datum.diff ?? null, event);
      })
      .on("mouseout", () => {
        this.hideTooltip();
      });

    // Y axis - dynamically scale based on max value to remove white space
    const maxValue = Math.max(...normalizedData.map(d => d.value));
    const yMax = Math.min(100, Math.ceil(maxValue * 1.15)); // ensure at least 100, with 15% padding
    const y = d3.scaleLinear().domain([0, yMax]).range([innerHeight, 0]);

    chart
      .append("g")
      .call(d3.axisLeft(y).ticks(5).tickFormat((d) => `${d}%` as any))
      .call((g) => g.select(".domain").remove())
      .selectAll("text")
      .style("fill", labelColor)
      .style("font-family", "Poppins, sans-serif")
      .style("font-weight", "400")
      .style("font-size", "12px")
      .style("line-height", "14px")
      .style("text-anchor", "end");

    // ✅ Horizontal dotted grid lines only
    chart
      .append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(y).tickSize(-innerWidth).tickFormat(() => "" as any))
      .selectAll("line")
      .style("stroke", "#DBDBDB")
      .style("stroke-dasharray", "3,3");

    chart.select(".grid").select(".domain").remove(); // remove vertical axis line

    // X axis baseline (left line)
    chart
      .append("line")
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("y1", 0)
      .attr("y2", innerHeight)
      .attr("stroke", "#DBDBDB")
      .attr("stroke-width", 1);

    // Y axis baseline (0 line)
    chart
      .append("line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", y(0))
      .attr("y2", y(0))
      .attr("stroke", "#DBDBDB")
      .attr("stroke-width", 1);

    // --- Bars ---
    const bars = chart
      .selectAll<SVGRectElement, ChartData>("rect.bar")
      .data(normalizedData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.label)! + x.bandwidth() * 0.2) // narrower bar centered
      .attr("y", innerHeight)
      .attr("width", x.bandwidth() * 0.6)
      .attr("height", 0)
      .attr("fill", barColor)
      .attr("ry", 4)
      // Fallback title
      .each(function (d) {
        const rect = d3.select(this as SVGRectElement);
        rect.select("title").remove();
        rect.append("title").text(`${d.label}: ${d.value}%`);
      })
      // Hover interactions: tooltip + highlight
      .on("mouseover", (event: MouseEvent, d: ChartData) => {
        this.showTooltip(d.label, d.value, d.diff ?? null, event);
        d3.select(event.currentTarget as SVGRectElement)
          .transition()
          .duration(150)
          .attr("fill", d3.color(barColor)!.darker(0.8).toString());
      })
      .on("mousemove", (event: MouseEvent, d: ChartData) => {
        this.showTooltip(d.label, d.value, d.diff ?? null, event);
      })
      .on("mouseout", (event: MouseEvent) => {
        this.hideTooltip();
        d3.select(event.currentTarget as SVGRectElement)
          .transition()
          .duration(150)
          .attr("fill", barColor);
      });

    // Animate bars growing
    bars
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attr("y", (d) => y(d.value))
      .attr("height", (d) => innerHeight - y(d.value));

    // Percentage labels on top of bars
    chart
      .selectAll<SVGTextElement, ChartData>(".value-label")
      .data(normalizedData)
      .enter()
      .append("text")
      .attr("class", "value-label")
      .attr("x", (d) => x(d.label)! + x.bandwidth() / 2)
      .attr("y", (d) => y(d.value) - 8)
      .attr("text-anchor", "middle")
      .style("fill", valueColor)
      .style("font-family", "Poppins, sans-serif")
      .style("font-weight", "400")
      .style("font-size", "12px")
      .style("line-height", "14px")
      .text((d) => `${d.value}%`)
      // Optional: tooltip on value labels too
      .on("mouseover", (event: MouseEvent, d: ChartData) => {
        this.showTooltip(d.label, d.value, d.diff ?? null, event);
      })
      .on("mousemove", (event: MouseEvent, d: ChartData) => {
        this.showTooltip(d.label, d.value, d.diff ?? null, event);
      })
      .on("mouseout", () => {
        this.hideTooltip();
      });
      const pillGroup = chart
      .selectAll(".diff-pill-group")
      .data(normalizedData)
      .enter()
      .append("g")
      .attr("class", "diff-pill-group")
      .attr(
        "transform",
        (d) =>
          `translate(${x(d.label)! + x.bandwidth() / 2}, ${y(d.value) - 28})`
      );

      pillGroup.each(function (d) {
        if (d.diff === null || d.diff === undefined) return;

        const g = d3.select(this);

        const text =
          d.diff === 0
            ? "● 0"
            : `${d.diff > 0 ? "▲" : "▼"} ${Math.abs(d.diff).toFixed(2)} `;

        const color =
          d.diff === 0 ? "#F57F17" : d.diff > 0 ? "#2E7D32" : "#C62828";

        const bg =
          d.diff === 0 ? "#FFF59D" : d.diff > 0 ? "#C8E6C9" : "#FFCDD2";

        const textNode = g
          .append("text")
          .attr("text-anchor", "middle")
          .style("font-family", "Poppins, sans-serif")
          .style("font-size", "10px")
          .style("font-weight", "500")
          .style("fill", color)
          .text(text);

        const bbox = (textNode.node() as SVGTextElement).getBBox();

        g.insert("rect", "text")
          .attr("x", bbox.x - 6)
          .attr("y", bbox.y - 3)
          .attr("width", bbox.width + 12)
          .attr("height", bbox.height + 6)
          .attr("rx", 6)
          .attr("fill", bg);
      });
  };

  render() {
    const { data } = this.props;
    if (!data || !data.length) {
      return (
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 120,
          color: "#707070",
          fontSize: 14,
          fontWeight: 500
        }}>
          <img
            src={noDataPlaceholder}
            alt="No Data"
          />
        </div>
      );
    }
    return <svg ref={this.svgRef} />;
  }
}

export default VerticalPercentageBarChart;
